import { getAuth } from 'firebase/auth';

/**
 * Check if auth is ready and user is available
 * @param {string} context - Context where this is being called
 * @returns {boolean} Whether auth is ready
 */
export function isAuthReady(context = 'unknown') {
  const auth = getAuth();
  const isReady = !!auth.currentUser;
  console.log(`[authUtils] Auth readiness check for ${context}:`, {
    isReady,
    uid: auth.currentUser?.uid || 'null',
    isAnonymous: auth.currentUser?.isAnonymous,
    timestamp: new Date().toISOString()
  });
  return isReady;
}

/**
 * Wait for auth to be ready with timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} context - Context where this is being called
 * @returns {Promise<boolean>} Whether auth became ready
 */
export function waitForAuth(timeoutMs = 5000, context = 'unknown') {
  console.log(`[authUtils] Waiting for auth to be ready for ${context}, timeout: ${timeoutMs}ms`);
  
  const auth = getAuth();
  
  // If already ready, return immediately
  if (auth.currentUser) {
    console.log(`[authUtils] Auth already ready for ${context}`);
    return Promise.resolve(true);
  }
  
  return new Promise((resolve) => {
    const startTime = performance.now();
    let hasResolved = false;
    
    const timeoutId = setTimeout(() => {
      if (!hasResolved) {
        const elapsed = performance.now() - startTime;
        console.warn(`[authUtils] Auth wait timeout for ${context} after ${elapsed}ms`);
        hasResolved = true;
        unsubscribe();
        resolve(false);
      }
    }, timeoutMs);
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!hasResolved && user) {
        const elapsed = performance.now() - startTime;
        console.log(`[authUtils] Auth became ready for ${context} after ${elapsed}ms`);
        hasResolved = true;
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

/**
 * Get Firebase ID token with comprehensive logging and timeout protection
 * @param {boolean} forceRefresh - Whether to force refresh the token
 * @param {string} context - Context/location where this is being called for logging
 * @returns {Promise<string>} The ID token
 */
export async function getIdTokenWithLogging(forceRefresh = false, context = 'unknown') {
  console.log(`[authUtils] Getting ID token for ${context}`, {
    forceRefresh,
    timestamp: new Date().toISOString()
  });

  const auth = getAuth();
  
  // Check if auth is ready
  if (!auth.currentUser) {
    console.warn(`[authUtils] No current user for ${context} - waiting for auth...`);
    
    // Wait for auth to be ready with timeout
    const authReady = await waitForAuth(3000, context);
    if (!authReady) {
      console.error(`[authUtils] Auth still not ready for ${context} after timeout`);
      throw new Error('User not authenticated - auth initialization timeout');
    }
  }

  console.log(`[authUtils] Current user info for ${context}:`, {
    uid: auth.currentUser.uid,
    email: auth.currentUser.email,
    isAnonymous: auth.currentUser.isAnonymous,
    displayName: auth.currentUser.displayName,
    emailVerified: auth.currentUser.emailVerified
  });

  try {
    const startTime = performance.now();
    
    // Add timeout protection for token retrieval
    const tokenPromise = auth.currentUser.getIdToken(forceRefresh);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Token retrieval timeout')), 5000);
    });
    
    const idToken = await Promise.race([tokenPromise, timeoutPromise]);
    const endTime = performance.now();
    
    console.log(`[authUtils] ID token retrieved successfully for ${context}:`, {
      tokenLength: idToken?.length || 0,
      forceRefresh,
      retrievalTimeMs: Math.round(endTime - startTime),
      tokenPreview: idToken ? `${idToken.substring(0, 20)}...` : 'null'
    });

    // Parse token payload for additional info (without verification - just for logging)
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      console.log(`[authUtils] Token payload info for ${context}:`, {
        iss: payload.iss,
        aud: payload.aud,
        auth_time: new Date(payload.auth_time * 1000).toISOString(),
        exp: new Date(payload.exp * 1000).toISOString(),
        iat: new Date(payload.iat * 1000).toISOString(),
        user_id: payload.user_id,
        firebase: payload.firebase
      });
    } catch (parseError) {
      console.warn(`[authUtils] Could not parse token payload for ${context}:`, parseError.message);
    }

    return idToken;
  } catch (error) {
    console.error(`[authUtils] Failed to get ID token for ${context}:`, {
      error: error.message,
      code: error.code,
      forceRefresh,
      uid: auth.currentUser?.uid
    });
    throw error;
  }
}

/**
 * Log current auth state for debugging
 * @param {string} context - Context where this is being called
 */
export function logCurrentAuthState(context = 'unknown') {
  const auth = getAuth();
  console.log(`[authUtils] Current auth state for ${context}:`, {
    hasCurrentUser: !!auth.currentUser,
    uid: auth.currentUser?.uid || 'null',
    email: auth.currentUser?.email || 'null',
    isAnonymous: auth.currentUser?.isAnonymous,
    emailVerified: auth.currentUser?.emailVerified,
    displayName: auth.currentUser?.displayName || 'null',
    lastSignInTime: auth.currentUser?.metadata?.lastSignInTime || 'null',
    creationTime: auth.currentUser?.metadata?.creationTime || 'null',
    providerData: auth.currentUser?.providerData?.map(p => ({
      providerId: p.providerId,
      uid: p.uid,
      email: p.email
    })) || [],
    config: {
      apiKey: auth.config.apiKey?.substring(0, 10) + '...',
      authDomain: auth.config.authDomain,
      projectId: auth.config.projectId
    }
  });
}

/**
 * Comprehensive auth diagnostic function to identify delays
 * @param {string} context - Context where this is being called
 */
export function diagnoseAuthDelay(context = 'auth-delay-diagnosis') {
  console.group(`[authUtils] 🔍 Auth Delay Diagnosis - ${context}`);
  
  const auth = getAuth();
  
  // Basic auth state
  console.log('1. Basic Auth State:', {
    hasAuth: !!auth,
    hasCurrentUser: !!auth.currentUser,
    authReady: !!auth.currentUser,
    timestamp: new Date().toISOString()
  });
  
  // Environment detection
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  const isLocalNetwork = window.location.hostname.startsWith('192.168.') ||
                        window.location.hostname.startsWith('10.') ||
                        (window.location.hostname.startsWith('172.') && 
                         parseInt(window.location.hostname.split('.')[1]) >= 16 && 
                         parseInt(window.location.hostname.split('.')[1]) <= 31);
  
  console.log('2. Environment:', {
    hostname: window.location.hostname,
    isLocalhost,
    isLocalNetwork,
    isLocalDevelopment: isLocalhost || isLocalNetwork,
    userAgent: navigator.userAgent,
    connectionType: navigator.connection?.effectiveType || 'unknown'
  });
  
  // Emulator connection state
  const emulatorState = {
    hasEmulatorConfig: !!auth.config?.emulator,
    emulatorHost: auth.config?.emulator?.host || 'not set',
    emulatorPort: auth.config?.emulator?.port || 'not set',
    emulatorUrl: auth.config?.emulator ? `http://${auth.config.emulator.host}:${auth.config.emulator.port}` : 'not configured'
  };
  console.log('3. Emulator State:', emulatorState);
  
  // Firebase config
  console.log('4. Firebase Config:', {
    projectId: auth.config.projectId,
    authDomain: auth.config.authDomain,
    apiKey: auth.config.apiKey?.substring(0, 10) + '...',
    appName: auth.app?.name || 'unknown'
  });
  
  // Browser storage state
  const storageTest = {
    localStorage: {
      available: typeof(Storage) !== "undefined" && !!window.localStorage,
      firebaseKeys: []
    },
    indexedDB: {
      available: !!window.indexedDB
    }
  };
  
  // Check for Firebase-related localStorage keys
  if (storageTest.localStorage.available) {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('firebase:') || key.includes('auth'))) {
        storageTest.localStorage.firebaseKeys.push(key);
      }
    }
  }
  
  console.log('5. Browser Storage:', storageTest);
  
  // Network connectivity test
  console.log('6. Network State:', {
    online: navigator.onLine,
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt
    } : 'not available'
  });
  
  // Test emulator connectivity if in development
  if (isLocalhost || isLocalNetwork) {
    const emulatorHost = isLocalhost ? '127.0.0.1' : window.location.hostname;
    const testUrls = [
      `http://${emulatorHost}:9099`,  // Auth emulator
      `http://${emulatorHost}:5001`,  // Functions emulator
      `http://${emulatorHost}:8080`,  // Firestore emulator
      `http://${emulatorHost}:4000`   // Emulator UI
    ];
    
    console.log('7. Testing Emulator Connectivity...');
    testUrls.forEach(url => {
      fetch(url, { method: 'HEAD', mode: 'no-cors' })
        .then(() => console.log(`✅ ${url} - reachable`))
        .catch(() => console.log(`❌ ${url} - not reachable`));
    });
  }
  
  // Performance timing
  const performanceTiming = performance.getEntriesByType('navigation')[0];
  if (performanceTiming) {
    console.log('8. Page Load Performance:', {
      domContentLoaded: Math.round(performanceTiming.domContentLoadedEventEnd - performanceTiming.navigationStart),
      loadComplete: Math.round(performanceTiming.loadEventEnd - performanceTiming.navigationStart),
      firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 'not available',
      firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 'not available'
    });
  }
  
  // Test auth state changes
  console.log('9. Starting Auth State Monitor...');
  let authStateChanges = 0;
  const authStartTime = performance.now();
  
  const unsubscribe = auth.onAuthStateChanged((user) => {
    authStateChanges++;
    const elapsed = performance.now() - authStartTime;
    console.log(`🔄 Auth State Change #${authStateChanges} after ${Math.round(elapsed)}ms:`, {
      hasUser: !!user,
      uid: user?.uid || 'null',
      isAnonymous: user?.isAnonymous,
      email: user?.email || 'null'
    });
    
    if (user || authStateChanges >= 3) {
      console.log('✅ Auth diagnosis complete');
      unsubscribe();
      console.groupEnd();
    }
  });
  
  // Auto-cleanup after 15 seconds
  setTimeout(() => {
    console.log('⏰ Auth diagnosis timeout - cleaning up');
    unsubscribe();
    console.groupEnd();
  }, 15000);
  
  console.log(`📊 Diagnosis started, monitoring for up to 15 seconds...`);
} 